import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Heart } from "lucide-react";

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const TIER_BUCKETS = [
  { name: "Visionary Circle ($25,000+)", min: 25000, color: "#FF00F5" },
  { name: "Champions ($10,000 – $24,999)", min: 10000, color: "#FFB800" },
  { name: "Patrons ($5,000 – $9,999)", min: 5000, color: "#00D9FF" },
  { name: "Advocates ($1,000 – $4,999)", min: 1000, color: "#7B2CFF" },
  { name: "Friends (Under $1,000)", min: 0, color: "#A3A3A3" },
];

const bucketFor = (amt) => TIER_BUCKETS.find((b) => (amt || 0) >= b.min);

export default function DonorRecognitionWall() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/donor-honor-roll").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data || (data.donors.length === 0 && data.anonymous_count === 0)) return null;

  const grouped = TIER_BUCKETS.map((b) => ({
    ...b,
    donors: data.donors.filter((d) => bucketFor(d.amount)?.name === b.name),
  })).filter((b) => b.donors.length > 0);

  return (
    <section data-testid="donor-honor-roll" className="relative py-20 md:py-24 bg-aawta-jet/80 border-t border-white/10 scroll-mt-nav">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="mb-10">
          <p className="text-overline text-aawta-pink mb-3 flex items-center gap-2"><Heart size={12} fill="currentColor" /> DONOR HONOR ROLL</p>
          <h2 className="font-display text-3xl md:text-5xl font-light text-white tracking-tight">
            With <em className="italic text-aawta-pink">deepest gratitude</em>.
          </h2>
          <p className="text-white/50 text-sm mt-3 max-w-2xl">
            Every name below has helped power the gala, the scholarships, and the workforce
            programs that make AAWTA possible. Thank you.
          </p>
        </div>

        <div className="space-y-10">
          {grouped.map((b) => (
            <div key={b.name} data-testid={`donor-bucket-${b.min}`}>
              <div className="flex items-baseline gap-3 mb-4">
                <p className="text-overline" style={{ color: b.color }}>{b.name}</p>
                <span className="text-xs text-white/40">{b.donors.length} donor{b.donors.length === 1 ? "" : "s"}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {b.donors.map((d) => (
                  <DonorCard key={d.id} donor={d} accent={b.color} />
                ))}
              </div>
            </div>
          ))}
          {data.anonymous_count > 0 && (
            <div className="border border-white/10 bg-aawta-charcoal/30 p-6 text-center" data-testid="anonymous-bucket">
              <p className="text-overline text-white/60 mb-2">ANONYMOUS GIVING</p>
              <p className="font-display text-2xl text-white">
                + {data.anonymous_count} generous donor{data.anonymous_count === 1 ? "" : "s"} who wish to remain anonymous.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function DonorCard({ donor, accent }) {
  return (
    <div
      data-testid={`donor-card-${donor.id}`}
      className="border border-white/10 bg-aawta-charcoal/40 p-4 flex items-center gap-3 hover:border-aawta-pink/40 transition-colors"
      style={{ borderLeftWidth: 3, borderLeftColor: accent }}
    >
      {donor.logo_url ? (
        <img src={donor.logo_url} alt={donor.name} className="h-12 w-12 object-contain bg-white/5 rounded" />
      ) : (
        <div className="h-12 w-12 rounded bg-white/5 flex items-center justify-center text-aawta-pink text-sm font-mono">
          {(donor.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("")}
        </div>
      )}
      <div className="min-w-0">
        <p className="text-sm text-white truncate">{donor.name}</p>
        {donor.organization && <p className="text-xs text-white/50 truncate">{donor.organization}</p>}
        {donor.donor_category && <p className="text-[10px] tracking-widest uppercase mt-0.5" style={{ color: accent }}>{donor.donor_category}</p>}
      </div>
    </div>
  );
}
